// FLOW-01 (task 5.4, D17, PLAN P23–P26) — a sessão de teste ao vivo, como no n8n, fora do componente.
//
// O `POST depurar` devolve o `sessao_id` na hora e o PowerTools roda em segundo plano; os eventos de
// cada passo chegam em lote pelo `FlowDepuracaoChannel`. Duas corridas moldam este módulo:
// - o primeiro lote pode ser transmitido ANTES de a assinatura existir (o PowerTools começa antes de
//   a resposta do POST voltar). O PowerTools grava cada lote na sessão antes de mandá-lo, então,
//   assinada a sessão, o `GET depurar/:sessao_id` traz o que o cabo perdeu;
// - os lotes chegam fora de ordem (cada um é um POST à plataforma), e o GET repete o que o cabo já
//   trouxe. Quem decide é o `seq` do evento: a união deduplica por ele (`unirEventos`).
// Sem cabo (sem conexão, ou a assinatura recusada), a sessão segue pelo GET a cada segundo, até o
// `fim`. Com cabo, também relê, devagar (a cada 5 s): o PowerTools só loga o POST de um lote que a
// plataforma recusou, e o lote perdido pode ser o do `fim` — sem a releitura, a tela ficaria
// "rodando" para sempre. A assinatura cai no `fim` (concluída, falhou, parada ou expirada) e ao
// sair da tela.
//
// As saídas fixadas seguem o `id` do passo (o nome do nó do n8n); pontos de parada, "executar até
// aqui" e "executar este passo" seguem o caminho (`steps[buscar]`), o id do nó.
//
// FLOW-11 (task 5.3, R20, G16): a desativação só no teste da D17 saiu. Desativar grava `disabled:
// true` no YAML e vale também publicado; o PowerTools pula o passo pelo YAML, e o pedido não manda
// mais a lista `desativados`. O `desativado` do nó vem do grafo (`grafoDoFlow`, `data.desativado`).
//
// FLOW-04 (PLAN P4): dentro de laço, o evento traz o caminho da volta (`steps[resolve]{2}.steps[b]`)
// e `volta` (`[2]`). O passo da tela é o nó, pelo caminho estático (`caminhoEstatico`), com a lista
// das voltas em que rodou (`voltas`), que o seletor de volta lê; "executar este passo" leva a
// volta escolhida.
//
// FLOW-12 (Task 4.4, G1, G2, G11, Q1, Q4): a sessão vem também do servidor e o pedido diz o modo.
// - `carregarRecentes` remonta o passo a passo ao abrir o editor (R4): as sessões da última hora, cada
//   passo pela sessão mais nova que o rodou, com o "Limpar execução" guardado no navegador (G11);
// - `iniciar({modo, alvo})`: o ▶ (`passo`) roda só o passo quando a entrada guardada vale pelas
//   pegadas (G2), e senão roda os de cima (`ate`); "Executar até aqui" é `passo_com_anteriores` e
//   "Executar passos anteriores" é `anteriores` (`antes_de`). O 409 da sessão que sumiu cai no `ate`,
//   uma vez, e a resposta sem o modo pedido para a sessão (G17);
// - Q1: o modo que roda o gatilho com `params` sem dados fixados (ou com fixados que não passam mais)
//   não roda: a sessão pede a gaveta (`gaveta`), e nunca vai com os params vazios.
// O pedido de antes (`{ate}` ou `{passo}`, sem `modo`) segue valendo como era até as telas da L5.
import { objetoLido } from './leituraYaml';
import { caminhoEstatico, estadosDaExecucao } from './execucaoAoVivo';
import { entradaValePelasPegadas } from './pegadasDoFlow';
import { conferirParams, valorInicial } from './regrasDoParametro';

const EVENTO_DO_CABO = 'flow_depuracao.eventos';

// Os passos que aceitam saída fixada: a MESMA lista do `FIXAVEIS` de `depuracao.py` (a spec confere
// as duas). Fixar um `if` decidiria o ramo por fora da condição; o laço fixado vale inteiro, não por
// volta. Os de `OBJETO` devolvem objeto, e a fixada também tem de ser (`_OBJETO`); o `code`
// devolve qualquer JSON.
export const FIXAVEIS = [
  'http',
  'tool',
  'filter',
  'sort',
  'date',
  'code',
  'set',
  'return',
  'respond',
  'for_each',
  'while',
  'aggregate',
];
const OBJETO = [
  'http',
  'tool',
  'filter',
  'sort',
  'date',
  'set',
  'for_each',
  'while',
  'aggregate',
];

// Os comandos da sessão (`depuracao.py`, `COMANDOS`): `proxima_volta` é o "continuar até a próxima
// volta" do laço, que pausa de novo no primeiro passo fora da volta em que a sessão está.
export const COMANDOS = [
  'continuar',
  'pausar',
  'proximo',
  'proxima_volta',
  'parar',
];

// O valor que a redação do PowerTools põe no lugar de um segredo resolvido (`sdk/ctx.py`).
export const SEGREDO = '$TOOL_SECRET';

// Os seqs da sessão anterior que ficam na tela no "executar este passo": negativos, para nunca
// colidirem com os da sessão nova e virem antes dela.
const ANTERIOR = 1_000_000;

const ehObjeto = valor =>
  !!valor && typeof valor === 'object' && !Array.isArray(valor);

export function unirEventos(atuais, novos) {
  const porSeq = new Map((atuais || []).map(evento => [evento.seq, evento]));
  (novos || []).forEach(evento => {
    if (evento && Number.isFinite(evento.seq)) porSeq.set(evento.seq, evento);
  });
  return [...porSeq.values()].sort((a, b) => a.seq - b.seq);
}

// 'concluido' | 'falhou' | 'parado' | 'expirado' | 'pausado' | 'rodando'. O `fim` traz o estado; o
// "executar até aqui" acaba como `concluido` com o run em `tool_error` (o depurador parou de
// propósito), e por isso só é falha a sessão que não teve `parado`.
export function faseDosEventos(eventos) {
  const fim = eventos.find(e => e.tipo === 'fim');
  const parado = eventos.find(e => e.tipo === 'parado');
  if (fim) {
    if (fim.estado !== 'concluido') return fim.estado;
    const ok = (fim.resultado?.outcome ?? 'ok') === 'ok';
    return ok || parado ? 'concluido' : 'falhou';
  }
  if (parado)
    return parado.estado === 'concluido' ? 'concluido' : parado.estado;
  return estadosDaExecucao(eventos).pausadoEm ? 'pausado' : 'rodando';
}

export const VIVAS = ['rodando', 'pausado'];

// A fase do teste na tela: nula antes de rodar, `iniciando` enquanto o POST não volta.
export function faseDoTeste(estado) {
  if (estado.iniciando) return 'iniciando';
  if (!estado.sessaoId) return null;
  if (estado.carregando) return 'iniciando';
  return faseDosEventos(estado.eventos);
}

export const eventosDaTela = estado => [
  ...estado.anteriores,
  ...estado.eventos,
];

const instante = evento => Date.parse(evento?.em);

// O carimbo mais recente de um lote, pelo relógio do servidor.
const ultimoInstante = eventos =>
  Math.max(...eventos.filter(e => e.seq > 0).map(instante));

// Do primeiro evento ao `fim`, ou até agora enquanto a sessão corre. Em pausa, o relógio do flow
// não anda (P24), e o da tela também não: para no `pausado`. Acabada ou pausada, a conta é entre
// dois carimbos do servidor, que é exata. Rodando, o "agora" é do navegador, e o relógio dele não
// bate com o do servidor (um desvio de 1 h dava "Rodando · 1758 min" ou um tempo negativo): quem
// conta é o `relogio` da sessão (`{local, servidor}`, guardado quando o primeiro lote chegou), do
// instante local da chegada, somado ao que o servidor já tinha andado até ali.
export function duracaoMs(eventos, agora = Date.now(), relogio = null) {
  const comeco = instante(eventos.find(e => e.seq > 0));
  if (Number.isNaN(comeco)) return null;
  const fim = eventos.find(e => e.tipo === 'fim');
  const pausa = [...eventos]
    .reverse()
    .find(e => ['pausado', 'retomado'].includes(e.tipo));
  let ms = agora - comeco;
  if (fim) ms = instante(fim) - comeco;
  else if (pausa?.tipo === 'pausado') ms = instante(pausa) - comeco;
  else if (relogio) ms = relogio.servidor - comeco + (agora - relogio.local);
  return Math.max(0, ms);
}

// O que um evento `passo` diz do estado do passo (o do nó, e o de cada volta dele).
const estadoDoEvento = evento => ({
  status: evento.status,
  fixado: !!evento.fixado,
  desativado: evento.status === 'skipped',
  duracao: evento.duracao_ms ?? null,
  itens: evento.itens ?? null,
  razao: evento.razao ?? null,
  // FLOW-05 (Gate 4): o aviso de um passo que terminou `ok` (ex.: anexos sem conversa).
  aviso: evento.aviso ?? null,
  httpStatus: evento.http_status ?? null,
  outcome: evento.outcome ?? null,
  gravou: !!evento.gravou,
  // Quando o último evento do passo chegou ("há 12 s", na caixa) e o erro para leigo (D19).
  em: evento.em ?? null,
  falha: evento.falha ?? null,
  // FLOW-07: os `console.log` e o pico de memória do passo `code`, que só o evento de fim traz.
  logs: evento.logs ?? null,
  memoriaPicoMb: evento.memoria_pico_mb ?? null,
  // FLOW-05 (task 4.1): o cartão de cada arquivo que o passo produziu ou entregou.
  arquivos: evento.arquivos ?? null,
});

// A volta de um passo do corpo de laço, na lista `voltas` do nó: a entrada e a saída daquela volta.
function registrarVolta(passo, evento) {
  const chave = evento.volta.join('.');
  let volta = passo.voltas.find(v => v.volta.join('.') === chave);
  if (!volta) {
    volta = {
      volta: [...evento.volta],
      caminho: evento.caminho,
      entrada: undefined,
      saida: undefined,
    };
    passo.voltas.push(volta);
  }
  if (evento.entrada !== undefined) volta.entrada = evento.entrada;
  if (evento.saida !== undefined) volta.saida = evento.saida;
  Object.assign(volta, estadoDoEvento(evento));
}

// Os passos que rodaram, na ordem em que começaram (a lista do painel), cada um com o último
// estado, a entrada do `running` e a saída do evento de fim; e o marco do `respond`: em quantos ms
// o agente recebeu a resposta, contados do primeiro evento. Os passos que COMEÇAM depois dele são a
// cauda, "depois de responder".
//
// FLOW-04: um passo por NÓ (caminho estático), com o estado da última volta e, em `voltas`, cada
// volta em que rodou, na ordem em que começou (`{volta, caminho, entrada, saida, status, …}`). O nó
// do laço guarda em `progresso` o último `{feitas, total}` que emitiu.
//
// FLOW-12 (R8, Gate 4): o alvo do "Executar passos anteriores" não roda, e não tem evento `passo`. A
// entrada que chegou nele vem no `parado` do portão dele (o PowerTools só a põe ali no `antes_de`), em
// `parada` (`{caminho, caminhoNaVolta, volta, entrada}`), para a caixa do passo mostrá-la.
export function passosExecutados(eventos) {
  const ordenados = [...eventos].sort((a, b) => a.seq - b.seq);
  const passos = [];
  const porCaminho = {};
  let resposta = null;
  let parada = null;
  ordenados.forEach(evento => {
    if (evento.tipo === 'parado' && evento.entrada !== undefined) {
      parada = {
        caminho: caminhoEstatico(evento.caminho),
        caminhoNaVolta: evento.caminho,
        volta: [...(evento.volta || [])],
        entrada: evento.entrada,
      };
      return;
    }
    if (evento.tipo === 'respondeu') {
      const comeco = instante(ordenados.find(e => e.seq > 0));
      resposta = {
        caminho: evento.caminho,
        valor: evento.valor,
        ms: Math.max(0, instante(evento) - comeco) || 0,
      };
      return;
    }
    if (evento.tipo !== 'passo') return;
    const caminho = caminhoEstatico(evento.caminho);
    let passo = porCaminho[caminho];
    if (!passo) {
      passo = {
        caminho,
        type: evento.type,
        depoisDeResponder: !!resposta,
        entrada: undefined,
        saida: undefined,
        // D21: o instante (do servidor) em que o passo começou, para a linha do tempo do painel.
        inicio: instante(evento),
        voltas: [],
        progresso: null,
      };
      porCaminho[caminho] = passo;
      passos.push(passo);
    }
    if (evento.voltas) passo.progresso = { ...evento.voltas };
    if (evento.volta?.length) registrarVolta(passo, evento);
    // FLOW-12 (Gate 4): o caminho do último evento, com a volta (`steps[l]{0}.steps[b]`): é por ele
    // que a sessão guarda a saída inteira do passo do corpo de um laço (`GET …/passo`). O caminho
    // do nó, sem a volta, dá 404 lá.
    passo.caminhoNaVolta = evento.caminho;
    if (evento.entrada !== undefined) passo.entrada = evento.entrada;
    if (evento.saida !== undefined) passo.saida = evento.saida;
    Object.assign(passo, estadoDoEvento(evento));
  });
  return { passos, resposta, parada };
}

// D21: a linha do tempo do teste, como o mockup `Teste.dc.html`. Cada passo que já tem duração
// vira uma barra: onde começou e quanto durou, em % da execução (do primeiro começo ao último
// fim). Sem carimbo de tempo ou sem duração, o passo fica sem barra.
export function linhaDoTempo(passos) {
  const medidos = passos.filter(
    p => Number.isFinite(p.inicio) && Number.isFinite(p.duracao)
  );
  if (!medidos.length) return {};
  const comeco = Math.min(...medidos.map(p => p.inicio));
  const fim = Math.max(...medidos.map(p => p.inicio + p.duracao));
  const total = Math.max(fim - comeco, 1);
  return Object.fromEntries(
    medidos.map(p => [
      p.caminho,
      {
        esquerda: ((p.inicio - comeco) / total) * 100,
        largura: Math.max((p.duracao / total) * 100, 1.5),
      },
    ])
  );
}

// O resultado que o agente recebeu (o `fim` traz a resposta inteira do teste), ou nulo antes do fim.
// FLOW-12 (Gate 4): nulo também no run que o depurador encerrou no portão (o `parado`: o "executar
// até", o "executar passos anteriores", o parar e o expirar). Ele sai `tool_error`, sem outcome novo
// (FLOW-01 P8), com a razão da parada como erro, mas nada foi devolvido a agente nenhum: a fase da
// sessão ("Concluído", "Parado") é que diz como ele acabou.
export function resultadoFinal(eventos) {
  if (eventos.some(e => e.tipo === 'parado')) return null;
  return eventos.find(e => e.tipo === 'fim')?.resultado || null;
}

// A amostra de um evento como a tela a mostra: o PowerTools corta em 8 KB (`{truncado, bytes,
// previa}`) e troca segredo por `$TOOL_SECRET`.
export function lerAmostra(valor) {
  if (ehObjeto(valor) && valor.truncado === true && 'previa' in valor) {
    return {
      truncado: true,
      bytes: valor.bytes ?? null,
      texto: String(valor.previa),
      redigido: String(valor.previa).includes(SEGREDO),
    };
  }
  const texto = JSON.stringify(valor ?? null, null, 2);
  return {
    truncado: false,
    bytes: null,
    texto,
    redigido: texto.includes(SEGREDO),
  };
}

// O corpo do `POST depurar`, nos nomes do controller. `ate` e `passo` não vão juntos (o PowerTools
// recusa): "executar este passo" ganha de "até aqui". Executar um passo só não para em ponto de
// parada: é aquele passo, com a entrada da sessão `base`.
//
// FLOW-12: `antesDe` ("Executar passos anteriores") também não vai com os outros dois, e as `pegadas`
// do editor (G2) vão em todo pedido que as tem.
export function corpoDoDepurar({
  params = {},
  contexto = {},
  agentSlug = null,
  pontos = [],
  ate = null,
  antesDe = null,
  passo = null,
  base = null,
  volta = null,
  conversaDeTeste = null,
  raizes = null,
  pegadas = null,
  dadosDoTeste = null,
}) {
  const corpo = {
    args: params,
    context: contexto,
    pontos: passo ? [] : [...pontos],
  };
  if (agentSlug) corpo.agent_slug = agentSlug;
  // FLOW-05 L9 (task 9.3): a conversa do teste vai fora do `context`; o Rails a confere na conta e
  // a põe lá como `conversation_id`.
  if (conversaDeTeste) corpo.conversa_de_teste = conversaDeTeste;
  // FLOW-10 (G17): as raízes do "Contexto do teste", pelos nomes das fórmulas; o Rails as repassa
  // e o PowerTools as converte como no turno real.
  if (raizes) corpo.raizes = raizes;
  if (passo) Object.assign(corpo, { passo, base });
  else if (ate) corpo.ate = ate;
  else if (antesDe) corpo.antes_de = antesDe;
  // FLOW-04: a volta do passo do corpo de laço (`[2]`); sem ela, o PowerTools roda a primeira.
  if (passo && volta) corpo.volta = [...volta];
  if (pegadas) corpo.pegadas = { ...pegadas };
  // FLOW-12 (R5, G6): os dados do teste, que o Rails salva antes de rodar.
  if (dadosDoTeste) corpo.dados_do_teste = dadosDoTeste;
  return corpo;
}

// O corpo do `POST comando`: o comando, os pontos de parada de agora, ou os dois.
export function corpoDoComando(comando, listas = {}) {
  const corpo = {};
  if (comando) corpo.comando = comando;
  if (listas.pontos) corpo.pontos = [...listas.pontos];
  return corpo;
}

// Os parâmetros de exemplo, a partir do `params` do YAML. O YAML que não lê dá `{}`: o painel não
// pode quebrar com ele. FLOW-12 (Task 4.5, T1, R7): o `example` do param, e sem ele o vazio (o
// `default`, que o schema não tem, saiu). O vazio do texto, da lista, do objeto e do toggle desligado
// fica no JSON, para quem o edita ver a chave; o número e a escolha ficam sem valor, porque o 0 e a
// primeira opção seriam um valor que ninguém escolheu, e o teste rodaria com ele (Q1).
const VAZIO_DO_TIPO = {
  string: () => '',
  boolean: () => false,
  array: () => [],
  object: () => ({}),
};
// O `params` do YAML (nome → definição), ou `{}` com o YAML que não lê.
export function definicoesDosParams(texto) {
  let params;
  try {
    params = objetoLido(texto)?.params;
  } catch {
    return {};
  }
  return ehObjeto(params) ? params : {};
}

export function exemploDosParams(texto) {
  const params = definicoesDosParams(texto);
  return Object.fromEntries(
    Object.entries(params).flatMap(([nome, def]) => {
      const d = ehObjeto(def) ? def : {};
      const inicial = valorInicial(d, undefined);
      if (inicial !== undefined) return [[nome, inicial]];
      if (Array.isArray(d.enum) && d.enum.length) return [];
      const vazio = VAZIO_DO_TIPO[d.type];
      return vazio ? [[nome, vazio()]] : [];
    })
  );
}

// Um texto JSON que tem de ser objeto (params e contexto). `{valor}` ou `{erro}`, com o erro na
// forma de uma chave de i18n do painel.
export function lerObjetoJson(texto) {
  if (!String(texto || '').trim()) return { valor: {} };
  try {
    const valor = JSON.parse(texto);
    return ehObjeto(valor) ? { valor } : { erro: 'NOT_OBJECT' };
  } catch (erro) {
    return { erro: 'INVALID_JSON', detalhe: erro.message };
  }
}

// A saída fixada que a pessoa editou, conferida como o PowerTools confere (`depuracao.validar`).
export function lerFixado(tipo, texto) {
  let valor;
  try {
    valor = JSON.parse(texto);
  } catch (erro) {
    return { erro: 'INVALID_JSON', detalhe: erro.message };
  }
  if (OBJETO.includes(tipo) && !ehObjeto(valor)) return { erro: 'NOT_OBJECT' };
  if (valor === null || (typeof valor === 'string' && !valor.trim())) {
    return { erro: 'EMPTY' };
  }
  return { valor };
}

// O que cada ação de teste do nó pode fazer agora; o motivo (chave de i18n) quando não pode.
// FLOW-12 (G14, T3): fixar não pede mais execução anterior, nem a saída inteira na amostra: o que o
// "Fixar" faz em cada caso é o `comoFixar`.
//
// Com uma sessão viva (`viva`), executar este passo e executar até aqui ficam desligados, como no
// n8n durante uma execução: abrir outra sessão largaria a viva sem ninguém vê-la.
export function acoesDoNo(
  data,
  { pontos = [], fixados = {}, comEntrada = [], viva = false }
) {
  const { caminho, idDoPasso, tipo } = data;
  const fixado = !!idDoPasso && idDoPasso in fixados;
  // Desafixar vale sempre; fixar, em todo passo de dados com id.
  let fixar = null;
  if (!FIXAVEIS.includes(tipo)) fixar = 'PIN_NOT_DATA';
  else if (!idDoPasso) fixar = 'PIN_NEEDS_ID';
  // FLOW-11 (5.5, S11): "Executar este passo" funciona sem sessão (sem execução anterior, a página
  // faz o "Executar até aqui"); o passo desativado ou solto não roda, nem sozinho.
  let executar = null;
  if (data.desativado || data.solto) executar = 'NOT_RUN';
  else if (viva) executar = 'RUN_WHILE_LIVE';
  return {
    ponto: pontos.includes(caminho),
    desativado: !!data.desativado,
    fixado,
    executar,
    ate: executar,
    // FLOW-12 (T3): "Executar passos anteriores" roda os de cima, mesmo com este passo desativado.
    anteriores: viva ? 'RUN_WHILE_LIVE' : null,
    fixar,
    // Sem a entrada de uma execução anterior, executar o passo é executar até ele.
    comEntrada: comEntrada.includes(caminho),
  };
}

// FLOW-12 (G14, T3): o que o "Fixar" do nó faz: desafixa o fixado; fixa a última saída que deu certo;
// com a amostra cortada em 8 KB, busca a saída inteira (`GET …/passo`, na sessão de onde veio o passo)
// e a fixa; sem saída, abre onde se escreve a saída fixada. `{acao, id, …}`, com `acao` em
// `desafixar`, `fixar` (com `valor`), `buscar` (com `sessaoId` e `caminho`) ou `escrever`.
export function comoFixar(
  data,
  { fixados = {}, passos = [], comEntrada = {} } = {}
) {
  const { caminho, idDoPasso: id } = data;
  if (id && id in fixados) return { acao: 'desafixar', id };
  const executado = passos.find(p => p.caminho === caminho);
  if (executado?.status !== 'ok') return { acao: 'escrever', id, caminho };
  if (!lerAmostra(executado.saida).truncado) {
    return { acao: 'fixar', id, valor: executado.saida };
  }
  const sessaoId = comEntrada[caminho] || null;
  // No corpo de um laço, a saída guardada é a da volta (`caminhoNaVolta`), e não a do nó.
  return sessaoId
    ? {
        acao: 'buscar',
        id,
        caminho: executado.caminhoNaVolta || caminho,
        sessaoId,
      }
    : { acao: 'escrever', id, caminho };
}

// FLOW-12 (Task 5.2, T2, PLAN §2.4): quantos passos rodam antes do alvo, contados antes do run, para
// a linha "Executando N passos antes de `avisa`…": os passos antes dele em cada lista do caminho até
// ele (o da própria lista e, subindo, os antes de cada bloco que o contém). Um bloco conta como um; o
// desativado, o solto e o de saída fixada não contam. `passos` são os nós do canvas
// (`grafoDoFlow(...).nos[].data`), e o passo é achado pelo caminho.
export function passosAntesDe(passos, caminho, fixados = {}) {
  const alvo = (passos || []).find(d => d.caminho === caminho);
  if (!alvo?.ponteiro) return 0;
  const porPonteiro = new Map(passos.map(d => [d.ponteiro, d]));
  const partes = alvo.ponteiro.split('/').slice(1);
  let total = 0;
  // Cada par (lista, índice) do caminho: `/steps` 3, `/steps/3/then` 1, ...
  for (let k = partes.length - 1; k >= 1; k -= 1) {
    const indice = Number(partes[k]);
    if (Number.isInteger(indice)) {
      const lista = `/${partes.slice(0, k).join('/')}`;
      for (let i = 0; i < indice; i += 1) {
        const irmao = porPonteiro.get(`${lista}/${i}`);
        const fixado = !!irmao?.idDoPasso && irmao.idDoPasso in fixados;
        if (irmao && !irmao.desativado && !irmao.solto && !fixado) total += 1;
      }
    }
  }
  return total;
}

// FLOW-12 (Q1, G13): os modos que rodam o gatilho, e por isso pedem os dados do teste. O `passo` com
// a entrada guardada não roda o gatilho.
export const MODOS_COM_GATILHO = ['flow', 'passo_com_anteriores', 'anteriores'];
export const precisaDoGatilho = modo => MODOS_COM_GATILHO.includes(modo);

// Q1 (Gate 1) e G13: por que abrir a gaveta "Dados do teste" antes de rodar, ou nulo para rodar. O
// modo que roda o gatilho, num flow com `params`, só roda com os dados fixados ou com o "Executar"
// da própria gaveta (`daGaveta`), e nos dois casos só se eles passam nas regras: nunca com os params
// vazios. Sem o agente, num flow que o pede, também (`agente`).
export function motivoDaGaveta(
  modo,
  {
    definicoes = null,
    params = {},
    fixado = false,
    daGaveta = false,
    pedeAgente = false,
    agente = '',
  } = {}
) {
  const temParams = ehObjeto(definicoes) && Object.keys(definicoes).length > 0;
  if (precisaDoGatilho(modo) && temParams) {
    if (!(fixado || daGaveta) || conferirParams(definicoes, params)) {
      return 'params';
    }
  }
  if (pedeAgente && !String(agente || '').trim()) return 'agente';
  return null;
}

// O erro de um pedido, para o painel: o código do servidor (`flow_nao_compilado`,
// `powertools_inalcancavel`, `agente_obrigatorio`…) e a mensagem dele, quando veio.
function erroDoPedido(erro) {
  const corpo = erro?.response?.data || {};
  const detalhe = corpo.detalhe;
  return {
    codigo: corpo.error || null,
    status: erro?.response?.status ?? null,
    mensagem:
      corpo.mensagem ||
      (typeof detalhe === 'string' ? detalhe : detalhe?.mensagem) ||
      corpo.motivo ||
      corpo.errors?.join(' | ') ||
      erro?.message ||
      null,
  };
}

// O controle da sessão. `api` são as chamadas já presas à conta e à entrada (`depurar(corpo)`,
// `comando(sessaoId, corpo)`, `depuracao(sessaoId)`); `assinar(sessaoId, {recebido, conectado,
// recusado})` devolve a assinatura (com `unsubscribe`) ou nulo sem cabo; `aoMudar` recebe o estado
// novo a cada mudança.
export function criarSessaoDeTeste({
  api,
  assinar,
  aoMudar,
  agendar = setTimeout,
  desagendar = clearTimeout,
  intervalo = 1000,
  intervaloComCabo = 5000,
  relogioLocal = () => Date.now(),
  // FLOW-12 (G11): onde o "Limpar execução" guarda a hora do clique (a chave, com a conta e a
  // entrada) e o armazenamento do navegador. Sem chave, não guarda.
  chaveDaLimpeza = () => null,
  armazenamento = () => window.localStorage,
}) {
  const INICIAL = {
    sessaoId: null,
    eventos: [],
    anteriores: [],
    gravam: [],
    // Os passos `tool` que o catálogo do agente afirmou serem de leitura (5c-03-a).
    leem: [],
    ignorados: [],
    // De qual sessão vem a entrada guardada de cada passo, para "executar este passo".
    comEntrada: {},
    // FLOW-12 (G2): as sessões conhecidas (`{sessao_id, criado_em, pegadas}`), do passo a passo e as
    // que a página abriu: o ▶ confere a entrada pelas pegadas da sessão de onde ela vem.
    sessoes: {},
    // O modo que a sessão aberta roda (`flow`, `passo`, `passo_com_anteriores`, `anteriores`).
    modo: null,
    // FLOW-12 (Task 5.2, T2): o caminho do passo que a sessão mira (o ▶, o "até aqui", o "antes de")
    // e, no fim, o `resumo` dela (G4): o fora do caminho e o passo que falhou, para o painel.
    alvo: null,
    resumo: null,
    // Q1: o pedido que espera a gaveta "Dados do teste" (`{motivo, pedido}`), ou nulo.
    gaveta: null,
    // A execução gravada aberta (`carregar`): o YAML, os params, o modo, o alvo e o resumo dela.
    daGravada: null,
    iniciando: false,
    carregando: false,
    gravada: false,
    erro: null,
    semCabo: false,
    // Quando o primeiro lote da sessão chegou, pelo relógio do navegador (`local`) e pelo carimbo
    // mais recente dele (`servidor`): a duração ao vivo conta daqui (`duracaoMs`).
    relogio: null,
  };
  let estado = INICIAL;
  let assinatura = null;
  let espera = null;

  const mudar = parcial => {
    estado = { ...estado, ...parcial };
    aoMudar(estado);
  };
  const terminou = () => estado.eventos.some(e => e.tipo === 'fim');

  function largar() {
    if (assinatura) assinatura.unsubscribe();
    assinatura = null;
    if (espera) desagendar(espera);
    espera = null;
  }

  // FLOW-12 (Task 5.2, T2, G10): o `resumo` da sessão que mirou um passo, para o painel dizer por que
  // o flow não passou por ele, ou qual passo de cima falhou. O PowerTools grava o resumo logo depois
  // de emitir o `fim`: a releitura que chega antes dele tenta de novo, poucas vezes.
  async function buscarResumo(sessaoId, tentativas = 3) {
    let corpo = null;
    try {
      corpo = await api.depuracao(sessaoId);
    } catch {
      return;
    }
    if (sessaoId !== estado.sessaoId) return;
    if (corpo?.resumo) {
      mudar({ resumo: corpo.resumo });
      return;
    }
    if (tentativas > 1) {
      agendar(() => buscarResumo(sessaoId, tentativas - 1), intervalo / 2);
    }
  }

  function absorver(sessaoId, novos) {
    if (sessaoId !== estado.sessaoId || !novos?.length) return;
    const comEntrada = { ...estado.comEntrada };
    novos.forEach(evento => {
      if (evento.tipo === 'passo' && evento.caminho) {
        comEntrada[caminhoEstatico(evento.caminho)] = sessaoId;
      }
      // FLOW-12 (T2, R3, R8, Gate 4): o alvo do "Executar passos anteriores" não roda, mas a entrada
      // dele fica guardada na sessão, e o `parado` a traz: o ▶ seguinte parte dela, sem rodar de
      // novo os de cima, como o "Execute step" do n8n. O `por_caminho` das recentes conta igual.
      if (
        evento.tipo === 'parado' &&
        evento.caminho &&
        evento.entrada !== undefined
      ) {
        comEntrada[caminhoEstatico(evento.caminho)] = sessaoId;
      }
    });
    const relogio =
      estado.relogio ||
      (novos.some(e => e.seq > 0) && !Number.isNaN(ultimoInstante(novos))
        ? { local: relogioLocal(), servidor: ultimoInstante(novos) }
        : null);
    mudar({
      eventos: unirEventos(estado.eventos, novos),
      comEntrada,
      relogio,
    });
    if (terminou()) {
      largar();
      if (estado.alvo && !estado.resumo && !estado.gravada) {
        buscarResumo(sessaoId);
      }
    }
  }

  async function reler(sessaoId) {
    try {
      const corpo = await api.depuracao(sessaoId);
      absorver(sessaoId, corpo?.eventos);
      return corpo;
    } catch (erro) {
      if (sessaoId === estado.sessaoId) mudar({ erro: erroDoPedido(erro) });
      return null;
    }
  }

  // Uma releitura agendada por vez: sem cabo a cada segundo, com cabo devagar.
  function sondar(sessaoId) {
    if (sessaoId !== estado.sessaoId || terminou() || espera) return;
    espera = agendar(
      async () => {
        espera = null;
        await reler(sessaoId);
        sondar(sessaoId);
      },
      estado.semCabo ? intervalo : intervaloComCabo
    );
  }

  function semCabo(sessaoId) {
    mudar({ semCabo: true });
    if (espera) desagendar(espera);
    espera = null;
    reler(sessaoId).then(() => sondar(sessaoId));
  }

  function acompanhar(sessaoId) {
    assinatura = assinar(sessaoId, {
      recebido: dados => {
        if (
          dados?.event === EVENTO_DO_CABO &&
          dados.data?.sessao_id === sessaoId
        ) {
          absorver(sessaoId, dados.data.eventos);
        }
      },
      // A cada (re)conexão: o que passou enquanto o cabo não estava.
      conectado: () => reler(sessaoId),
      recusado: () => {
        assinatura = null;
        if (sessaoId === estado.sessaoId) semCabo(sessaoId);
      },
    });
    if (!assinatura) semCabo(sessaoId);
    else sondar(sessaoId);
  }

  const viva = () => !estado.gravada && VIVAS.includes(faseDoTeste(estado));

  // -- o "Limpar execução" (G11), no navegador, com try/catch: sem ele a tela só não esconde nada.
  function lerLimpeza() {
    try {
      const chave = chaveDaLimpeza();
      const valor = chave ? armazenamento()?.getItem(chave) : null;
      const ms = Number(valor);
      return valor && Number.isFinite(ms) ? ms : null;
    } catch {
      return null;
    }
  }
  function gravarLimpeza(ms) {
    try {
      const chave = chaveDaLimpeza();
      if (chave) armazenamento()?.setItem(chave, String(ms));
    } catch {
      // O navegador sem armazenamento (aba anônima, cota): a tela limpa agora e volta ao recarregar.
    }
  }

  const conhecer = (sessoes, lista) =>
    lista.reduce(
      (mapa, s) => ({
        ...mapa,
        [s.sessao_id]: {
          sessao_id: s.sessao_id,
          criado_em: s.criado_em,
          pegadas: s.pegadas || {},
        },
      }),
      sessoes
    );

  // O pedido de agora (G1, G2): o modo e os campos do `depurar`. O ▶ (`passo`) usa a entrada guardada
  // quando ela vale pelas pegadas, e senão roda os de cima (`ate`). Sem `modo`, o pedido de antes.
  function plano(opcoes = {}) {
    const { modo, alvo = null } = opcoes;
    if (modo === undefined) {
      if (opcoes.passo) {
        return {
          modo: 'passo',
          passo: opcoes.passo,
          base: estado.comEntrada[opcoes.passo] || null,
        };
      }
      if (opcoes.ate) return { modo: 'passo_com_anteriores', ate: opcoes.ate };
      return { modo: 'flow' };
    }
    if (modo === 'passo') {
      const base = estado.comEntrada[alvo] || null;
      const sessao = base && estado.sessoes[base];
      const vale =
        !!sessao &&
        entradaValePelasPegadas(opcoes.pegadas, alvo, sessao, relogioLocal());
      return vale
        ? { modo: 'passo', passo: alvo, base }
        : { modo: 'passo_com_anteriores', ate: alvo };
    }
    if (modo === 'passo_com_anteriores') return { modo, ate: alvo };
    if (modo === 'anteriores') return { modo, antesDe: alvo };
    return { modo: 'flow' };
  }

  // A gaveta no lugar do run (Q1): o pedido fica guardado para o "Executar" dela.
  function pedirGaveta(pedido, opcoes) {
    const motivo = motivoDaGaveta(pedido.modo, opcoes);
    if (motivo) mudar({ iniciando: false, gaveta: { motivo, pedido: opcoes } });
    return !!motivo;
  }

  // `opcoes`: os campos de `corpoDoDepurar`, e desde a FLOW-12 o `modo` e o `alvo`, as `pegadas` de
  // agora e o que a gaveta precisa (`definicoes`, `fixado`, `daGaveta`, `pedeAgente`; ver
  // `motivoDaGaveta`). Executar um passo só mantém na tela o resto da sessão anterior, como o n8n
  // mantém os dados dos outros nós. A sessão ainda viva para antes: largada, ela seguiria rodando (e
  // gravando) no PowerTools, ou pausada até vencer, sem aparecer na tela. Se o `parar` não chega, a
  // nova não abre; 404 e 409 dizem que ela já acabou. Devolve se a sessão abriu.
  //
  // Gate 4 (2ª rodada): a gaveta que pediu este run fica aberta até a sessão abrir. Ela fechava no
  // começo do pedido, e um 422 do servidor deixava a tela sem a gaveta e sem dizer nada (o erro ia
  // para o painel, que só abre com o run). Recusado, o pedido fica na gaveta, com o erro (`erro`).
  async function iniciar(opcoes = {}) {
    if (estado.iniciando) return false;
    let pedido = plano(opcoes);
    if (pedirGaveta(pedido, opcoes)) return false;
    const aParar = viva() ? estado.sessaoId : null;
    mudar({ iniciando: true, erro: null });
    if (aParar) {
      try {
        await api.comando(aParar, corpoDoComando('parar'));
      } catch (erro) {
        if (![404, 409].includes(erro?.response?.status)) {
          mudar({ iniciando: false, erro: erroDoPedido(erro) });
          return false;
        }
      }
    }
    largar();
    const naTela = eventosDaTela(estado);
    const anterioresDe = ({ passo }) =>
      passo
        ? naTela
            .filter(
              e => e.tipo === 'passo' && caminhoEstatico(e.caminho) !== passo
            )
            .map((e, i) => ({ ...e, seq: i - ANTERIOR }))
        : [];
    const pedir = () =>
      api.depurar(
        corpoDoDepurar({
          ...opcoes,
          passo: pedido.passo || null,
          base: pedido.base || null,
          ate: pedido.ate || null,
          antesDe: pedido.antesDe || null,
        })
      );
    let resposta;
    try {
      resposta = await pedir();
    } catch (erro) {
      // G2: a sessão da entrada sumiu (409): roda os de cima, uma vez, com a regra da gaveta (Q1).
      const sumiu =
        opcoes.modo !== undefined &&
        pedido.modo === 'passo' &&
        erro?.response?.status === 409;
      if (!sumiu) {
        mudar({ iniciando: false, erro: erroDoPedido(erro) });
        return false;
      }
      pedido = { modo: 'passo_com_anteriores', ate: pedido.passo };
      if (pedirGaveta(pedido, opcoes)) return false;
      try {
        resposta = await pedir();
      } catch (outro) {
        mudar({ iniciando: false, erro: erroDoPedido(outro) });
        return false;
      }
    }
    // G17: o PowerTools que não devolve o modo pedido é o antigo, que ignora o `antes_de` e rodaria o
    // flow inteiro. A sessão para na hora, e a tela mostra o erro e o que chegou a rodar.
    const semModo = opcoes.modo !== undefined && resposta.modo !== pedido.modo;
    if (semModo) {
      try {
        await api.comando(resposta.sessao_id, corpoDoComando('parar'));
      } catch {
        // 404 e 409: ela já acabou; o fim chega pela releitura.
      }
    }
    mudar({
      iniciando: false,
      gaveta: null,
      sessaoId: resposta.sessao_id,
      eventos: [],
      relogio: null,
      anteriores: anterioresDe(pedido),
      gravam: resposta.gravam || [],
      leem: resposta.leem || [],
      ignorados: resposta.fixados_ignorados || [],
      gravada: false,
      semCabo: false,
      modo: resposta.modo ?? null,
      // Só o pedido com modo (as telas da L5) mira um passo: o de antes segue como era.
      alvo:
        opcoes.modo === undefined
          ? null
          : pedido.passo || pedido.ate || pedido.antesDe || null,
      resumo: null,
      sessoes: conhecer(estado.sessoes, [
        {
          sessao_id: resposta.sessao_id,
          criado_em: new Date(relogioLocal()).toISOString(),
          pegadas: opcoes.pegadas,
        },
      ]),
      erro: semModo
        ? {
            codigo: 'modo_nao_aceito',
            status: null,
            mensagem: null,
            pedido: pedido.modo,
            recebido: resposta.modo ?? null,
          }
        : null,
    });
    acompanhar(resposta.sessao_id);
    return true;
  }

  // R4, G11: o passo a passo ao abrir o editor. As sessões da última hora (as mais novas primeiro) e o
  // `por_caminho` remontam o `comEntrada` e a tela como ela estava: a sessão mais nova é a da tela, e
  // cada passo que outra rodou por último entra antes dela, como o "executar este passo" mantém os
  // outros. As sessões de antes do "Limpar execução" não voltam à tela, mas a entrada delas vale para
  // o ▶ (a SPEC limpa sem excluir a sessão). A mais nova ainda viva volta a ser acompanhada.
  async function carregarRecentes() {
    if (!api.passoAPasso || estado.iniciando || viva()) return false;
    let corpo;
    try {
      corpo = await api.passoAPasso();
    } catch (erro) {
      mudar({ erro: erroDoPedido(erro) });
      return false;
    }
    const sessoes = Array.isArray(corpo?.sessoes) ? corpo.sessoes : [];
    const porCaminho = ehObjeto(corpo?.por_caminho) ? corpo.por_caminho : {};
    const limpa = lerLimpeza();
    const naTela = sessoes.filter(
      s => !limpa || !(Date.parse(s.criado_em) <= limpa)
    );
    const base = {
      comEntrada: { ...porCaminho },
      sessoes: conhecer(estado.sessoes, sessoes),
    };
    if (!naTela.length) {
      mudar(base);
      return true;
    }
    const [maisNova, ...outras] = naTela;
    const anteriores = [...outras]
      .reverse()
      .flatMap(s =>
        (s.eventos || [])
          .filter(
            e =>
              e.tipo === 'passo' &&
              typeof e.caminho === 'string' &&
              porCaminho[caminhoEstatico(e.caminho)] === s.sessao_id
          )
          .sort((a, b) => a.seq - b.seq)
      )
      .map((e, i) => ({ ...e, seq: i - ANTERIOR }));
    largar();
    mudar({
      ...base,
      sessaoId: maisNova.sessao_id,
      eventos: unirEventos([], maisNova.eventos),
      anteriores,
      relogio: null,
      gravada: false,
      semCabo: false,
      erro: null,
      alvo: null,
      resumo: null,
    });
    if (!terminou()) acompanhar(maisNova.sessao_id);
    return true;
  }

  // T4: "Limpar execução" tira os status da tela sem excluir a sessão: o ▶ continua usando a entrada
  // guardada, e a hora do clique fica no navegador para o passo a passo não trazer de volta o que
  // foi limpo. Com a sessão viva, nada.
  function limparExecucao() {
    if (viva() || estado.gravada) return false;
    largar();
    gravarLimpeza(relogioLocal());
    mudar({
      sessaoId: null,
      eventos: [],
      anteriores: [],
      relogio: null,
      gravam: [],
      leem: [],
      ignorados: [],
      modo: null,
      alvo: null,
      resumo: null,
      erro: null,
    });
    return true;
  }

  // Comando a uma sessão que já acabou volta 409 (ou 404, vencida): a tela relê e mostra o fim.
  async function comandar(comando) {
    if (!estado.sessaoId || estado.gravada) return;
    const { sessaoId } = estado;
    try {
      await api.comando(sessaoId, corpoDoComando(comando));
    } catch (erro) {
      mudar({ erro: erroDoPedido(erro) });
      if ([404, 409].includes(erro?.response?.status)) {
        largar();
        reler(sessaoId);
      }
    }
  }

  // Pontos de parada mudados com a sessão rodando valem do próximo passo em diante.
  async function atualizarListas(listas) {
    if (!viva()) return;
    try {
      await api.comando(estado.sessaoId, corpoDoComando(null, listas));
    } catch (erro) {
      mudar({ erro: erroDoPedido(erro) });
    }
  }

  // "Depurar no editor": a sessão gravada, só leitura. FLOW-12 (G12): com o que ela rodou, para a
  // aba Execuções abrir o canvas do YAML dela e o "Usar estes dados" (`daGravada`).
  async function carregar(sessaoId) {
    largar();
    mudar({
      sessaoId,
      eventos: [],
      relogio: null,
      anteriores: [],
      gravada: true,
      carregando: true,
      erro: null,
      daGravada: null,
    });
    const corpo = await reler(sessaoId);
    mudar({
      carregando: false,
      daGravada:
        corpo && sessaoId === estado.sessaoId
          ? {
              flowYaml: corpo.flow_yaml ?? null,
              estado: corpo.estado ?? null,
              params: corpo.params ?? null,
              modo: corpo.modo ?? null,
              alvo: corpo.alvo ?? null,
              resumo: corpo.resumo ?? null,
            }
          : null,
    });
  }

  // Uma sessão dublada, para a story: os eventos como se tivessem chegado. FLOW-12: `extra` traz o
  // que a sessão do servidor traria (as `sessoes` com as pegadas, o `modo`, o `alvo`, o `resumo`).
  function mostrar(sessaoId, eventos, extra = {}) {
    largar();
    mudar({
      sessaoId,
      eventos: [],
      relogio: null,
      anteriores: [],
      gravada: false,
      ...extra,
    });
    absorver(sessaoId, eventos);
  }

  // Sair da execução gravada: a tela volta a antes de rodar.
  function limpar() {
    largar();
    mudar(INICIAL);
  }

  return {
    iniciar,
    plano,
    comandar,
    atualizarListas,
    carregar,
    carregarRecentes,
    limparExecucao,
    fecharGaveta: () => mudar({ gaveta: null }),
    mostrar,
    limpar,
    sair: largar,
    estado: () => estado,
  };
}
